import { makeAutoObservable, observable, runInAction } from 'mobx';
import type { ICustomFilter } from '../../ui/data-table/components/filters/filters.models';
import {
    fileCabinetDocumentTypeFilter,
    fileCabinetListRequest,
    fileCabinetSearchFilter,
    FileCabinetTableRequest,
} from '../../services/file-cabinet/file-cabinet.query';
import type { FileCabinetLocation } from '../../services/file-cabinet/file-cabinet.location';
import {
    FileCabinetReference,
    FileCabinetReferences,
    fileCabinetLoadReferences,
    fileCabinetReferenceSummary,
    fileCabinetSameEndpoint,
} from '../../services/file-cabinet/file-cabinet.references';
import {
    CabinetDocument,
    fileCabinetRecordId,
    FileCabinetDataSource,
    FileCabinetDirection,
    FileCabinetChannel,
    FileCabinetListKey,
    FileCabinetPage,
    FileCabinetRecord,
    FileCabinetRequestError,
    FileCabinetViewKey,
} from '../../services/file-cabinet/file-cabinet.types';
import {
    FILE_CABINET_CHANNELS,
    FILE_CABINET_QUICK_TYPES,
    FILE_CABINET_VIEWS,
    FileCabinetSearch,
    fileCabinetSearchFor,
    FileCabinetView,
    fileCabinetViewFor,
} from '../../services/file-cabinet/file-cabinet.views';

/**
 * State for the File Cabinet screen (FE-225): which table, what the search box and type shortcut
 * hold, the page those produce, the counts on the direction toggle and type shortcuts, and the
 * record the drawer has open.
 *
 * **Paging, sorting, the Filters panel and columns belong to the table**, as on the Roster: the
 * core `DataTable` keeps them and reports a request through `onFetch`, and `loadPage` takes it.
 *
 * **Unknown is not zero (NS-3).** A count is a number only once a response says so. While it loads
 * or after it fails it is `undefined` — the UI shows `—` — and a 403 is `'no-access'`: this user
 * cannot read that channel, which says nothing about whether it is empty.
 *
 * Imports are leaf modules, so the store is tested under node.
 */

export type FileCabinetCount = number | 'no-access' | undefined;

export interface FileCabinetLoadError {
    message: string;
    /** 403: the account cannot read this list. Said as such, never as "nothing here". */
    forbidden: boolean;
}

/**
 * What carries each document on the page — the "Attached to" column. Loaded after the page, one
 * request per list for every document on it.
 */
export interface FileCabinetAttachments {
    /** The page it was loaded for, so a late answer for another page is dropped. */
    documentIds: string[];
    byDocument: Record<string, FileCabinetReference[]>;
    loading: boolean;
    /**
     * Every list answered, in full. False after a failure, a 403, or a list cut at its page limit — an
     * empty cell is then unknown, not "attached to nothing" (NS-3).
     */
    complete: boolean;
}

export interface FileCabinetDrawerState {
    view: FileCabinetViewKey;
    record: FileCabinetRecord;
}

export interface FileCabinetStoreConfig {
    dataSource: FileCabinetDataSource;
}

type DirectionalChannel = Exclude<FileCabinetChannel, 'docs'>;

export class FileCabinetStore {
    public readonly dataSource: FileCabinetDataSource;

    public channel: FileCabinetChannel = 'docs';
    /**
     * The direction last picked per channel, so switching tabs and back keeps it. Opening values are
     * the design's: received mail and faxes, sent email (the only kind there is), outbound calls.
     */
    public directions: Record<DirectionalChannel, FileCabinetDirection> = { mail: 'in', fax: 'in', email: 'out', call: 'out' };
    public search = '';
    /** All Documents only. Undefined is "All types". */
    public documentType: string | undefined = undefined;

    /** The page on screen. Undefined while the first page loads and after a failure. */
    public page: FileCabinetPage<FileCabinetRecord> | undefined = undefined;
    public loading = false;
    public error: FileCabinetLoadError | undefined = undefined;
    /**
     * Whether the page on screen came from a filtered request — search, shortcut or Filters panel. The
     * panel belongs to the table, so this is how the store knows an empty page means "no matches"
     * rather than "nothing here". Read from the request, not the search box: while a cleared search
     * waits out the table's debounce, the page shown is still the filtered one.
     */
    public lastRequestFiltered = false;

    /** Totals per table, over everything — for the direction toggle. */
    public viewCounts: Partial<Record<FileCabinetViewKey, FileCabinetCount>> = {};
    /** Totals per document type, for the shortcuts. `all` is every document. */
    public typeCounts: Record<string, FileCabinetCount> = {};

    public drawer: FileCabinetDrawerState | undefined = undefined;
    /**
     * A linked record (`?open=`) that did not open: none with that key, or the request failed. Said, so a
     * link that leads nowhere is not mistaken for a page that ignored it.
     */
    public linkError: string | undefined = undefined;

    /** All Documents only. */
    public attachments: FileCabinetAttachments | undefined = undefined;

    private pageRequest = 0;
    private attachmentsRequest = 0;
    private linkRequest = 0;
    /** Bumped when the counts are cleared, so a count asked before that cannot land after it. */
    private countsGeneration = 0;
    private lastRequest: FileCabinetTableRequest | undefined = undefined;
    /** Set when the page closes; nothing is requested after it. */
    private disposed = false;
    /** Counts being fetched, so a tab switch during a load does not ask twice. */
    private readonly countsInFlight = new Set<string>();
    /**
     * Lists that answered 403 this visit — permissions do not change while the page is open, so they
     * are not asked again on every page of documents; they are reported "no access" instead.
     */
    private readonly forbiddenLists = new Set<FileCabinetListKey>();

    constructor(config: FileCabinetStoreConfig) {
        this.dataSource = config.dataSource;
        makeAutoObservable<
            FileCabinetStore,
            'pageRequest' | 'attachmentsRequest' | 'linkRequest' | 'countsGeneration' | 'lastRequest' | 'countsInFlight' | 'forbiddenLists' | 'disposed'
        >(
            this,
            {
                dataSource: false,
                pageRequest: false,
                attachmentsRequest: false,
                linkRequest: false,
                countsGeneration: false,
                lastRequest: false,
                countsInFlight: false,
                forbiddenLists: false,
                disposed: false,
                // Records are the API's, read-only here: kept as given, not deep-proxied.
                page: observable.ref,
                attachments: observable.ref,
                drawer: observable.ref,
            },
            { autoBind: true },
        );
    }

    public get view(): FileCabinetViewKey {
        return fileCabinetViewFor(this.channel, this.channel === 'docs' ? undefined : this.directions[this.channel]);
    }

    public get viewDef(): FileCabinetView {
        return FILE_CABINET_VIEWS[this.view] as FileCabinetView;
    }

    /** What the search box searches here — the view's own search, or what this source can do instead. */
    public get searchDef(): FileCabinetSearch {
        return fileCabinetSearchFor(this.viewDef, this.dataSource.capabilities);
    }

    public get rows(): FileCabinetRecord[] {
        return this.page?.items ?? [];
    }

    public get total(): number | undefined {
        return this.page?.total;
    }

    /** The search box and type shortcut as table filters, for the table to send and page by. */
    public get ownFilters(): ICustomFilter[] {
        const filters: ICustomFilter[] = [];
        const search = fileCabinetSearchFilter(this.searchDef, this.search);
        if (search) {
            filters.push(search);
        }
        if (this.view === 'docs') {
            const type = fileCabinetDocumentTypeFilter(this.documentType);
            if (type) {
                filters.push(type);
            }
        }
        return filters;
    }

    /** A new tab starts clean: the design clears search, shortcut and drawer on every switch. */
    public setChannel(channel: FileCabinetChannel): void {
        if (channel === this.channel) {
            return;
        }
        this.channel = channel;
        this.resetForNewView();
        void this.loadCounts();
    }

    public setDirection(direction: FileCabinetDirection): void {
        if (this.channel === 'docs' || this.directions[this.channel] === direction) {
            return;
        }
        // A disabled direction (inbound email) has no table to switch to.
        const def = FILE_CABINET_CHANNELS.find(c => c.channel === this.channel);
        if (!def?.directions.some(d => d.direction === direction && d.view)) {
            return;
        }
        this.directions = { ...this.directions, [this.channel]: direction };
        this.resetForNewView();
    }

    public setSearch(search: string): void {
        this.search = search;
    }

    public setDocumentType(type: string | undefined): void {
        this.documentType = type;
    }

    /** The table's last request again — the error banner's Retry. */
    public reload(): Promise<void> {
        return this.lastRequest ? this.loadPage(this.lastRequest, this.view) : Promise.resolve();
    }

    /**
     * Called by the table whenever its page, size, sort or filters change, with the view it was
     * mounted for. A request for another view is dropped: the table debounces its fetches and does
     * not cancel a pending one when it unmounts, so after a quick tab switch the OLD table's request
     * still arrives — with the old view's filters, which must never be sent to the new view's list.
     */
    public async loadPage(request: FileCabinetTableRequest, view: FileCabinetViewKey): Promise<void> {
        if (this.disposed || view !== this.view) {
            return;
        }
        this.lastRequest = request;
        const ticket = ++this.pageRequest;
        this.loading = true;
        this.error = undefined;
        this.lastRequestFiltered = request.validFilters.length > 0;
        try {
            const page = await this.dataSource.list(view, fileCabinetListRequest(FILE_CABINET_VIEWS[view] as FileCabinetView, request));
            if (ticket !== this.pageRequest) {
                return;
            }
            runInAction(() => {
                this.page = page;
                this.loading = false;
            });
            if (view === 'docs') {
                void this.loadAttachments(page.items.map(record => fileCabinetRecordId('docs', record)).filter(Boolean) as string[]);
            }
        } catch (error) {
            this.noteForbidden(view, error);
            if (ticket !== this.pageRequest) {
                return;
            }
            runInAction(() => {
                this.page = undefined;
                this.loading = false;
                this.error = describe(error);
            });
        }
    }

    /**
     * Counts for what the current tab shows: each direction's total, or on All Documents the total
     * and one per type shortcut. One `limit=1` request each, read from `pagination-total`. A count
     * already known is kept — they are over everything, not the current search.
     */
    public async loadCounts(): Promise<void> {
        if (this.disposed) {
            return;
        }
        const jobs: Promise<void>[] = [];
        if (this.channel === 'docs') {
            jobs.push(this.countInto('type', 'all', 'docs', {}));
            for (const type of FILE_CABINET_QUICK_TYPES) {
                jobs.push(this.countInto('type', type, 'docs', { filterDocumentType: [`EQ:${type}`] }));
            }
        } else {
            const def = FILE_CABINET_CHANNELS.find(c => c.channel === this.channel);
            for (const { view } of def?.directions ?? []) {
                if (view) {
                    jobs.push(this.countInto('view', view, view, {}));
                }
            }
        }
        await Promise.all(jobs);
    }

    /** "Attached to" for the documents on the page. Lazy: only after the page itself is on screen. */
    public async loadAttachments(documentIds: string[]): Promise<void> {
        const ticket = ++this.attachmentsRequest;
        if (documentIds.length === 0) {
            this.attachments = { documentIds, byDocument: {}, loading: false, complete: true };
            return;
        }
        this.attachments = { documentIds, byDocument: {}, loading: true, complete: true };
        const found = await this.referencesOf(documentIds, 100);
        if (ticket !== this.attachmentsRequest || this.disposed) {
            return;
        }
        const byDocument: Record<string, FileCabinetReference[]> = {};
        for (const [documentId, references] of Object.entries(found.byDocument)) {
            byDocument[documentId] = references.map(fileCabinetReferenceSummary);
        }
        runInAction(() => {
            this.attachments = { documentIds, byDocument, loading: false, complete: found.unchecked.length === 0 && !found.truncated };
        });
    }

    /**
     * What carries these documents — the drawer's "Referenced by" and the table's "Attached to" both ask
     * here, so a list that answered 403 for one is not asked again by the other.
     */
    public async referencesOf(documentIds: string[], limit: number): Promise<FileCabinetReferences> {
        const found = await fileCabinetLoadReferences(this.dataSource, documentIds, { limit, skip: this.forbiddenLists });
        for (const { list, forbidden } of found.unchecked) {
            if (forbidden) {
                fileCabinetSameEndpoint(list).forEach(same => this.forbiddenLists.add(same));
            }
        }
        return found;
    }

    /** Opens a record of another table — a reference in the drawer. */
    public openReference(reference: FileCabinetReference): void {
        this.linkRequest++;
        if (reference.listed === false) {
            // No table lists it (a call with no known method): its drawer, over the table already shown.
            this.drawer = { view: reference.view, record: reference.record };
            return;
        }
        const [channel, direction] = reference.view.split('.') as [FileCabinetChannel, FileCabinetDirection];
        this.setChannel(channel);
        if (direction) {
            this.setDirection(direction);
        }
        this.drawer = { view: reference.view, record: reference.record };
    }

    public openRecord(record: FileCabinetRecord): void {
        this.linkRequest++;
        this.drawer = { view: this.view, record };
    }

    public closeDrawer(): void {
        this.linkRequest++;
        this.drawer = undefined;
    }

    /** Where the page is, for the URL — tab, direction, open record. Never the search (see `file-cabinet.location.ts`). */
    public get location(): FileCabinetLocation {
        const id = this.drawer ? fileCabinetRecordId(this.drawer.view, this.drawer.record) : undefined;
        return {
            channel: this.channel,
            direction: this.channel === 'docs' ? undefined : this.directions[this.channel],
            open: this.drawer && id ? { view: this.drawer.view, id } : undefined,
        };
    }

    /** Goes where a link says: its tab and direction, then the record it names, fetched by its key. */
    public applyLocation(location: FileCabinetLocation): Promise<void> {
        this.setChannel(location.channel ?? 'docs');
        if (location.direction) {
            this.setDirection(location.direction);
        }
        if (!location.open) {
            this.closeDrawer();
            return Promise.resolve();
        }
        const { view, id } = location.open;
        if (this.drawer && this.drawer.view === view && fileCabinetRecordId(view, this.drawer.record) === id) {
            return Promise.resolve();
        }
        return this.openById(view, id);
    }

    /** A record by its key — a link to it. Not found and failed are each said (NS-3); neither opens a drawer. */
    public async openById(view: FileCabinetViewKey, id: string): Promise<void> {
        const ticket = ++this.linkRequest;
        this.linkError = undefined;
        try {
            const record = await this.dataSource.get(view, id);
            if (ticket !== this.linkRequest || this.disposed) {
                return;
            }
            runInAction(() => {
                if (record) {
                    this.drawer = { view, record };
                } else {
                    this.linkError = 'The linked record was not found. It may have been removed, or the link is for another account.';
                }
            });
        } catch (error) {
            if (ticket !== this.linkRequest || this.disposed) {
                return;
            }
            runInAction(() => {
                this.linkError = `The linked record could not be loaded. ${describe(error).message}`;
            });
        }
    }

    public dismissLinkError(): void {
        this.linkError = undefined;
    }

    /**
     * Opens the document a mail, letter or fax record carries, in All Documents — where its own
     * record, claims and file live. The record already embeds it, so nothing is fetched.
     */
    public openDocument(document: CabinetDocument): void {
        this.setChannel('docs');
        this.linkRequest++;
        this.drawer = { view: 'docs', record: document };
    }

    /**
     * After something was added — an upload. It can finish long after the upload dialog closed, so the
     * page stays where the user is: same tab, search and drawer. The counts are asked again, and the
     * page too when it is All Documents, where an upload lands; another tab's list did not change.
     * After the page closed it asks for nothing (`reload` and `loadCounts` check).
     */
    public refresh(): void {
        this.countsGeneration++;
        this.countsInFlight.clear();
        this.viewCounts = {};
        this.typeCounts = {};
        if (this.channel === 'docs') {
            // The table's last request is a documents request: ask it again.
            void this.reload();
        }
        void this.loadCounts();
    }

    /** The page opened (again — a hot reload keeps the store but re-runs the page's effects). */
    public activate(): void {
        this.disposed = false;
    }

    /** The page closed: drop whatever is still pending and request nothing more. */
    public dispose(): void {
        this.disposed = true;
        this.pageRequest++;
        this.loading = false;
    }

    private resetForNewView(): void {
        // A linked record still loading belongs to the place the user just left.
        this.linkRequest++;
        this.search = '';
        this.documentType = undefined;
        this.drawer = undefined;
        this.page = undefined;
        this.error = undefined;
        // A response still on its way for the old view must not land in the new one.
        this.pageRequest++;
        this.loading = false;
        this.lastRequestFiltered = false;
        this.attachmentsRequest++;
        this.attachments = undefined;
    }

    private noteForbidden(list: FileCabinetListKey, error: unknown): void {
        if (error instanceof FileCabinetRequestError && error.forbidden) {
            fileCabinetSameEndpoint(list).forEach(same => this.forbiddenLists.add(same));
        }
    }

    private async countInto(
        target: 'view' | 'type',
        key: string,
        view: FileCabinetViewKey,
        filters: Record<string, string[]>,
    ): Promise<void> {
        const counts = target === 'view' ? this.viewCounts : this.typeCounts;
        const generation = this.countsGeneration;
        const flightKey = `${generation}:${target}:${key}`;
        // A number or "no access" is settled for the visit; only an unknown count is asked again.
        if ((counts as Record<string, FileCabinetCount>)[key] !== undefined || this.countsInFlight.has(flightKey)) {
            return;
        }
        this.countsInFlight.add(flightKey);
        let count: FileCabinetCount;
        try {
            // The source adds the view's call method itself.
            const page = await this.dataSource.list(view, { limit: 1, offset: 0, filters });
            count = page.total;
        } catch (error) {
            this.noteForbidden(view, error);
            count = error instanceof FileCabinetRequestError && error.forbidden ? 'no-access' : undefined;
        } finally {
            this.countsInFlight.delete(flightKey);
        }
        if (generation !== this.countsGeneration || this.disposed) {
            return;
        }
        runInAction(() => {
            if (target === 'view') {
                this.viewCounts = { ...this.viewCounts, [key]: count };
            } else {
                this.typeCounts = { ...this.typeCounts, [key]: count };
            }
        });
    }
}

function describe(error: unknown): FileCabinetLoadError {
    if (error instanceof FileCabinetRequestError) {
        return { message: error.message, forbidden: error.forbidden };
    }
    return { message: error instanceof Error ? error.message : String(error), forbidden: false };
}
